import * as THREE from "three";
import { PerspectiveCamera, Sprite} from "three";
import { Subject } from "rxjs";
import CharacterAnimations from "./Constants/CharacterAnimations";
import { CollisionPlane } from "./CollisionPlane";
import Menu from "./Menu";
import Movement from "./Movement";
import Coordinate from "./Enums/Coordinate";
import Direction from "./Enums/Direction";
import Speed from "./Enums/Speed";
import ZPosition from "./Enums/ZPosition";

class Player extends Movement {
  sprite: Sprite;
  exclamationMark: THREE.Sprite;
  playerYIndex$ = new Subject<number>();
  camera: PerspectiveCamera;
  menu: Menu;
  collisions: CollisionPlane[] = [];

  private isMoving = false;
  private isMenuOpen = false;
  private isSprintToggled = false;
  private direction = Direction.Up;
  private movementInterval?: NodeJS.Timer;
  private interactionCheckInterval?: NodeJS.Timer;
  private exclamationBobInterval?: NodeJS.Timer;
  private exclamationBobCounter = 0;
  private activeInteractable: CollisionPlane | null = null;

  private idlingTexture = new THREE.TextureLoader().load('./assets/characters/player/idling.png');
  private runningTexture = new THREE.TextureLoader().load('./assets/characters/player/running.png');
  private readingTexture = new THREE.TextureLoader().load('./assets/characters/player/reading.png');
  private exclamationTexture = new THREE.TextureLoader().load('./assets/characters/interactions/exclamation_mark.png');
  private idleTimer?: NodeJS.Timer;
  private idleDuration = 0;
  private animationInterval!: NodeJS.Timer;
  private currentRunningAnimationIndex = 0;

  private keyDownMap = new Map<Direction, boolean>();
  private keyDirectionMap = new Map<string, Direction>([
    ['ArrowUp', Direction.Up],
    ['W', Direction.Up],
    ['w', Direction.Up],
    ['ArrowDown', Direction.Down],
    ['S', Direction.Down],
    ['s', Direction.Down],
    ['ArrowLeft', Direction.Left],
    ['A', Direction.Left],
    ['a', Direction.Left],
    ['ArrowRight', Direction.Right],
    ['D', Direction.Right],
    ['d', Direction.Right],
  ]);

  constructor(camera: PerspectiveCamera, menu: Menu, unsubscribeNotifier$: Subject<void>) {
    const x = 0;
    const y = 2.5;
    const width = 1;
    const height = 2;
    const yOffset = -.75;
    super(new CollisionPlane({
        x: x,
        y: y,
        width: width,
        height: height / 4,
        yOffset: yOffset
      }),
      new CollisionPlane({
        x: x,
        y: y,
        width: 3 * width,
        height: 1.5 * height,
        yOffset: yOffset,
        color: 'blue'
      }));

    this.speed = Speed.Walk;
    this.sprite = new THREE.Sprite();
    this.sprite.geometry.computeBoundingBox();
    this.sprite.scale.set(width, height, 1);
    this.sprite.position.set(x, y, ZPosition.Player);

    this.exclamationTexture.magFilter = THREE.NearestFilter;
    this.exclamationMark = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.exclamationTexture }));
    this.exclamationMark.scale.set(0.6, 0.6, 1);
    this.exclamationMark.position.set(x, y + this.exclamationMark.scale.y, ZPosition.DynamicObjectAbove); //TODO: finalize position after adjusting image and scale
    this.exclamationMark.visible = false;

    this.camera = camera;
    this.menu = menu;

    this.idlingTexture.repeat.set(1 / CharacterAnimations.MovementIndexes, 1);
    this.idlingTexture.magFilter = THREE.NearestFilter;
    this.runningTexture.repeat.set(1 / CharacterAnimations.MovementIndexes, 1);
    this.runningTexture.magFilter = THREE.NearestFilter;
    this.readingTexture.repeat.set(1 / CharacterAnimations.ReadingIndexes, 1);
    this.readingTexture.magFilter = THREE.NearestFilter;

    this.setIdleAnimation();
    this.setCharacterMovementEventListeners();
    this.toggleMenu();
    this.interactionCheckInterval = setInterval(() => this.checkInteractions(), 50);
    this.exclamationBobInterval = setInterval(() => {
      const baseY = this.sprite.position.y + this.exclamationMark.scale.y;
      this.exclamationMark.position.setY(baseY + Math.sin(this.exclamationBobCounter) * 0.08);
      this.exclamationBobCounter += 0.09;
    }, 16);
    unsubscribeNotifier$.subscribe(() => {
      clearInterval(this.idleTimer);
      clearInterval(this.movementInterval);
      clearInterval(this.animationInterval);
      clearInterval(this.interactionCheckInterval);
      clearInterval(this.exclamationBobInterval);
      this.playerYIndex$.complete();
    });
  }

  private setCharacterMovementEventListeners(): void {
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (this.isMenuOpen) return;
      const direction = this.keyDirectionMap.get(event.key);
      if (direction) {
        this.changeDirection(direction);
      }
      if (event.key === ' ' && this.activeInteractable) {
        console.log('interacted with:', this.activeInteractable.userData());
      }
    });

    document.addEventListener('keyup', (event: KeyboardEvent) => {
      const direction = this.keyDirectionMap.get(event.key);
      if (direction) {
        this.keyDownMap.set(direction, false);
        const oldDirection = Array.from(this.keyDownMap.entries()).find((entry) => entry[1]);
        if (oldDirection && oldDirection[1]) {
          this.changeDirection(oldDirection[0]);
        } else {
          this.isMoving = false;
          this.setIdleAnimation();
        }
      }
      if (event.key === "Shift") {
        this.isSprintToggled = !this.isSprintToggled;
        this.speed = this.isSprintToggled ? Speed.Run : Speed.Walk;
        if (this.isMoving) {
          this.setRunAnimation(false);
        }
      }
      if (event.key === "Enter") this.toggleMenu();
    });
  }

  private toggleMenu(): void {
    if (this.isMenuOpen) {
      this.menu.close();
    } else {
      this.isMoving = false;
      this.setIdleAnimation();
      this.menu.open(this.sprite.position.x, this.sprite.position.y);
    }
    this.isMenuOpen = !this.isMenuOpen;
  }

  private setIdleAnimation(): void {
    if (this.movementInterval) clearInterval(this.movementInterval);
    clearInterval(this.animationInterval);
    this.idlingTexture.offset.setX(CharacterAnimations.IdlingCycles[this.direction][0] / CharacterAnimations.MovementIndexes);
    this.sprite.material.map = this.idlingTexture;

    let tileIndexes: number[];
    switch (this.direction) {
      case Direction.Up:
        tileIndexes = CharacterAnimations.IdlingCycles.Up;
        break;
      case Direction.Down:
        tileIndexes = CharacterAnimations.IdlingCycles.Down;
        break;
      case Direction.Left:
        tileIndexes = CharacterAnimations.IdlingCycles.Left;
        break;
      case Direction.Right:
        tileIndexes = CharacterAnimations.IdlingCycles.Right;
        break;
    }

    let counter = 0;
    this.animationInterval = setInterval(() => {
      this.idlingTexture.offset.setX(tileIndexes[counter] / CharacterAnimations.MovementIndexes);
      ++counter;
      if (counter >= tileIndexes.length) {
        counter = 0;
      }
    }, 250);

    if (this.idleTimer) clearInterval(this.idleTimer);
    this.idleTimer = setInterval(() => {
      ++this.idleDuration;
      if (this.idleDuration >= 30) {
        this.setReadingAnimation();
      }
    }, 1000)
  }

  private setReadingAnimation(): void {
    clearInterval(this.animationInterval);
    clearInterval(this.idleTimer);
    this.sprite.material.map = this.readingTexture;

    let intervalCounter = 0;
    this.animationInterval = setInterval(() => {
      this.readingTexture.offset.setX(CharacterAnimations.ReadingCycles[intervalCounter] / CharacterAnimations.ReadingIndexes);
      ++intervalCounter;
      if (intervalCounter >= CharacterAnimations.ReadingCycles.length) {
        intervalCounter = 0;
      }
    }, 200);
  }

  private setRunAnimation(resetAnimationIndex = true): void {
    clearInterval(this.animationInterval);
    clearInterval(this.idleTimer);

    if (resetAnimationIndex) {
      this.currentRunningAnimationIndex = 0;
    }
    this.idleDuration = 0;
    this.runningTexture.offset.setX(CharacterAnimations.RunningCycles[this.direction][0] / CharacterAnimations.MovementIndexes);
    this.sprite.material.map = this.runningTexture;

    let tileIndexes: number[];
    switch (this.direction) {
      case Direction.Up:
        tileIndexes = CharacterAnimations.RunningCycles.Up;
        break;
      case Direction.Down:
        tileIndexes = CharacterAnimations.RunningCycles.Down;
        break;
      case Direction.Left:
        tileIndexes = CharacterAnimations.RunningCycles.Left;
        break;
      case Direction.Right:
        tileIndexes = CharacterAnimations.RunningCycles.Right;
        break;
    }

    this.animationInterval = setInterval(() => {
      this.runningTexture.offset.setX(tileIndexes[this.currentRunningAnimationIndex] / CharacterAnimations.MovementIndexes);
      ++this.currentRunningAnimationIndex;
      if (this.currentRunningAnimationIndex >= tileIndexes.length) {
        this.currentRunningAnimationIndex = 0;
      }
    }, this.isSprintToggled ? 80 : 120);
  }

  private changeDirection(direction: Direction): void {
    this.keyDownMap.set(direction, true);
    this.move(direction);
  }

  private move(direction: Direction): void {
    if (this.direction !== direction || !this.isMoving) {
      this.direction = direction;
      this.isMoving = true;
      this.setRunAnimation();
      clearInterval(this.movementInterval);
      this.movementInterval = setInterval(() => this.determineMovement(direction), 20);
    }
  }

  private moveCharacter(direction: Direction): void {
    const coordinate = this.determineCoordinate(direction);
    const position = this.newPosition(direction, this.sprite.position);
    if (coordinate === Coordinate.Y) this.moveCharacterY(position);
    if (coordinate === Coordinate.X) this.moveCharacterX(position);
  }

  private moveCharacterY(y: number): void {
    this.sprite.position.setY(y);
    this.camera.position.setY(y);
    this.interactionRange.move(Coordinate.Y, y);
    this.collisionPlane.move(Coordinate.Y, y, true);
    this.playerYIndex$.next(this.sprite.position.y);
  }

  private moveCharacterX(x: number): void {
    this.sprite.position.setX(x);
    this.camera.position.setX(x);
    this.interactionRange.move(Coordinate.X, x);
    this.collisionPlane.move(Coordinate.X, x);
    this.exclamationMark.position.setX(x);
  }

  private determineMovement(direction: Direction): void {
    if (!this.hasCollision(direction))
    {
      this.moveCharacter(direction);
    } else {
      this.sprite.position.setX(this.collisionPlane.position(Coordinate.X));
      this.sprite.position.setY(this.collisionPlane.position(Coordinate.Y) - this.collisionPlane.yOffset);
    }
  }

  private hasCollision(direction: Direction, collisionAction?: (collision: CollisionPlane) => void): boolean {
    const coordinate = this.determineCoordinate(direction);
    collisionAction = collisionAction ?? ((collision: CollisionPlane) => this.collisionAction(direction, coordinate, collision));
    this.collisionPlane.move(coordinate, this.newPosition(direction, this.collisionPlane.mesh.position), false);
    return this.detectCollision(this.collisions, direction, collisionAction)
  }

  private collisionAction(direction: Direction, coordinate: Coordinate, collision: CollisionPlane): void {
    this.collisionPlane.move(coordinate, this.collisionLimit(collision, direction), false);
    for (let pass = 0; pass < 3; pass++) {
      this.collisionPlane.detectWorld();
      let anyResolved = false;

      for (const wall of this.collisions) {
        wall.detectWorld();
        if (!this.collisionPlane.intersects(wall)) continue;

        const px = this.collisionPlane.position(Coordinate.X);
        const py = this.collisionPlane.position(Coordinate.Y);
        const ps = this.collisionPlane.size();
        const wx = wall.position(Coordinate.X);
        const wy = wall.position(Coordinate.Y);
        const ws = wall.size();

        const xOverlap = Math.min(px + ps.width / 2, wx + ws.width / 2) - Math.max(px - ps.width / 2, wx - ws.width / 2);
        const yOverlap = Math.min(py + ps.height / 2, wy + ws.height / 2) - Math.max(py - ps.height / 2, wy - ws.height / 2);

        if (xOverlap <= yOverlap) {
          const sign = wx > px ? -1 : 1;
          this.collisionPlane.move(Coordinate.X, px + sign * (xOverlap + this.speed / 2));
        } else {
          const sign = wy > py ? -1 : 1;
          this.collisionPlane.move(Coordinate.Y, py + sign * (yOverlap + this.speed / 2), false);
        }
        anyResolved = true;
      }
      if (!anyResolved) break;
    }
    clearInterval(this.movementInterval);
  }

  private checkInteractions(): void {
    this.interactionRange.detectWorld();
    let found: CollisionPlane | null = null;
    for (const collision of this.collisions) {
      if (collision.isInteractable()) {
        collision.detectWorld();
        if (this.interactionRange.intersects(collision)) {
          found = collision;
          break;
        }
      }
    }

    if (found !== this.activeInteractable) {
      if (this.activeInteractable) {
        const prev = this.activeInteractable.userData().interactionIndicator as THREE.Sprite | undefined;
        if (prev) prev.visible = false;
      }
      this.activeInteractable = found;
    }

    this.exclamationMark.visible = found !== null;
    if (found) {
      const indicator = found.userData().interactionIndicator as THREE.Sprite | undefined;
      if (indicator) indicator.visible = true;
    }
  }
}
export default Player;